import {useCallback} from "react";
import useStaffUsersQuery from "@components/common/hooks/api/useStaffUsersQuery";
import { getFullName, toUserOption } from "@lib/user";

const useStaffUserLookup = () => {
  const { data: staffUsers, ...staffUsersQuery } = useStaffUsersQuery();

  /**
   * Takes an email address and returns the matching user object from the staff users list, if it exists.
   * @param string The user's email address
   * @return The full user object, or null if not found.
   */
  const getUser = useCallback((email) => {
    if (!staffUsers) {
      return;
    }
    return staffUsers.find(u => u.email === email);
  }, [staffUsers]);

  /**
   * Takes an email address and returns the user name from the staff users list, if it exists.
   * @param string The user's email address
   * @return The user's full name
   */
  const getUserName = useCallback((email) => {
    const user = getUser(email);
    return user ? getFullName(user) : email;
  }, [getUser]);

  /**
   * Takes an email address and returns the matching user object from the staff users list, if it exists.
   * @param string The user's email address
   * @return An "option" object with `id` and `label` properties, for use with dropdown form fields
   */
  const getUserOption = useCallback((email) => {
    if (staffUsers) {
      const user = staffUsers.find(u => u.email === email);
      return toUserOption(user);
    }
    // If not found, we still want to return a fully formatted option object. Use the email address as the label.
    return {
      id: email,
      label: email
    };
  }, [staffUsers]);

  /**
   * Builds an array of option objects from the list of collaborators on a session. Options will be sorted by name.
   * Used to set the values in forms with multi-value autocomplete fields.
   */
  const mapCollaboratorsToOptions = useCallback(collaborators => {
    if (!collaborators) {
      return [];
    }
    return collaborators.map(c => getUserOption(c.email));
  }, [staffUsers]);

  /**
   * Sets users' names in the metadata fields, e.g., created_by_name, updated_by_name.
   * Works for both events and sessions.
   * Note: This mutates the original object.
   * @param obj The event or session object
   */
  const setMetadataNames = useCallback((obj) => {
    if (!staffUsers) {
      return;
    }
    ['created_by', 'updated_by', 'canceled_by', 'completed_by'].forEach((attr) => {
      if (obj[attr]) {
        const user = getUser(obj[attr]);
        obj[`${attr}_names`] = user ? getFullName(user) : obj[attr];
      }
    });
  }, [getUser, staffUsers]);

  return {
    isStaffError: staffUsersQuery.isError,
    staffError: staffUsersQuery.error,
    isStaffLoading: staffUsersQuery.isLoading,
    isStaffRefetching: staffUsersQuery.isRefetching,
    getUser,
    getUserName,
    getUserOption,
    mapCollaboratorsToOptions,
    setMetadataNames
  }
}

export default useStaffUserLookup;
